import React from "react";
import { Dispatch, SetStateAction, useMemo, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Drawer,
  Select as MuiSelect,
  Divider,
  Button,
  SelectChangeEvent,
  IconButton,
  Typography,
  Stack,
  ListItemIcon,
  ListItemText,
  MenuItem as MuiMenuItem,
  MenuList,
  Menu,
} from "@mui/material";
import {
  CircleUserRound,
  HandPlatter,
  LaptopMinimal,
  Library,
  // MessageCircleQuestion,
  Salad,
  Settings2,
  ShieldCheck,
  Utensils,
  PieChart,
  Store,
  ChevronDown,
  User,
  X,
  FileQuestion,
} from "lucide-react";
import { styled } from "@mui/material/styles";
import useOrganizations from "@/features/organizations/useOrganizations";
import { useAccessControl } from "@/features/access-control";
import Logo from "@/assets/logos/bitelink-logo-light.svg";
import { setActiveOrganization } from "@/store/uiSlice";
import { logOut, passwordReset } from "@/features/auth/slice";
import { useAppDispatch, useAppSelector, useResponsive } from "@/hooks";
import routes from "@/constants/routes";
import useSnackbar from "@/hooks/useSnackbar";
import { useTour } from "@reactour/tour";

interface Props {
  isSidebarExpanded: boolean;
  setIsSidebarExpanded: Dispatch<SetStateAction<boolean>>;
}

const MenuItem = styled(MuiMenuItem)({
  "&": {
    paddingTop: 12,
    paddingBottom: 12,
    border: "1px solid transparent",
    borderRadius: 8,

    "&:hover": {
      backgroundColor: "#ffffff10",
    },
  },
  "&.Mui-selected": {
    borderColor: "#ffffff50",
    backgroundColor: "transparent",

    ".MuiListItemText-root": {
      opacity: 1,
    },
  },
  "& svg": {
    stroke: "white",
    strokeWidth: 1.5,
  },
  ".MuiListItemText-root": {
    opacity: 0.5,
  },
});

const Select = styled(MuiSelect)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  ".MuiSelect-select": {
    color: "white",
    fontWeight: 300,
    paddingLeft: 16,
    paddingRight: 16,
  },
  "& .MuiSelect-icon": {
    color: "white",
    right: 16,
  },
}));

const Sidebar = ({ isSidebarExpanded, setIsSidebarExpanded }: Props) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { pathname } = useLocation();
  const [openSnackbar] = useSnackbar();
  const { isMobile } = useResponsive();
  const { hasAccess } = useAccessControl();
  const { organizationId } = useParams<{ organizationId: string }>();
  const { organizations, currentOrganization } = useOrganizations();
  const profile = useAppSelector((state) => state.auth.user);
  const [anchorElUser, setAnchorElUser] = React.useState<null | HTMLElement>(null);
  const { setIsOpen } = useTour();

  const isActive = (path: string) => {
    const pathLength = path.split("/").filter((p) => p !== "").length;
    const urlPaths = pathname.split("/").filter((p) => p !== "");
    const currentPath = urlPaths.slice(urlPaths.length - pathLength, urlPaths.length).join("/");
    return currentPath === path;
  };

  const sidebarMenu = useMemo(() => {
    const menuItems = [
      {
        label: "Pedidos",
        slug: "pedidos",
        // slug: 'ordenes/asignadas',
        icon: <HandPlatter />,
        divider: true,
        hidden: !hasAccess("view.order"),
        order: 1,
      },
      {
        label: "Detalles",
        slug: "detalles",
        icon: <Store />,
        hidden: !hasAccess("view.organization"),
        order: 2,
        dataTour: "organization-details",
      },
      {
        label: "Contenido",
        slug: "contenido/publicados",
        icon: <Library />,
        hidden: !hasAccess("view.content"),
        order: 4,
        dataTour: "organization-content",
      },
      {
        label: "FAQs",
        slug: "faqs",
        icon: <FileQuestion />,
        // icon: <MessageCircleQuestion />,
        hidden: !hasAccess("view.faq"),
        order: 7,
      },
      {
        label: "Accesos",
        slug: "accesos",
        icon: <ShieldCheck />,
        hidden: !hasAccess("view.access"),
        order: 8,
      },
      {
        label: "Sitio Web",
        slug: "websites",
        icon: <LaptopMinimal />,
        hidden: !hasAccess("view.website"),
        order: 9,
      },
      {
        label: "Encuestas",
        slug: "surveys",
        icon: <PieChart />,
        hidden: !hasAccess("view.survey"),
        order: 10,
      },
      {
        label: "Configuraciones",
        slug: "configuraciones",
        icon: <Settings2 />,
        hidden: !hasAccess("update.organization"),
        order: 11,
        dataTour: "organization-settings",
      },
    ];

    if (currentOrganization?.industry === "food-and-beverages") {
      menuItems.push(
        {
          label: "Mesas",
          slug: "mesas",
          icon: <Utensils />,
          hidden: !hasAccess("view.table"),
          order: 5,
        },
        {
          label: "Menús",
          slug: "menus",
          icon: <Salad />,
          hidden: !hasAccess("view.menu"),
          order: 3,
          dataTour: "organization-menu",
        },
        {
          label: "Avatars",
          slug: "avatars",
          icon: <CircleUserRound />,
          hidden: !hasAccess("view.avatar"),
          order: 6,
        }
      );
    }

    return menuItems
      .filter(({ hidden }) => !hidden)
      .sort((a, b) => {
        if (a.order < b.order) return -1;
        if (a.order > b.order) return +1;
        return 0;
      });
  }, [currentOrganization?.industry, hasAccess]);

  useEffect(() => {
    const appSettings = JSON.parse(localStorage.getItem("bl") ?? "{}");

    if (!appSettings?.tourAdminLayout) {
      setIsOpen(true);
    }

    appSettings["tourAdminLayout"] = true;
    window.localStorage.setItem("bl", JSON.stringify(appSettings));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const userSettings = useMemo(
    () => [
      {
        label: "Contraseña",
        fn: async () => {
          try {
            await dispatch(passwordReset(profile?.email)), openSnackbar("¡Email enviado, revisa tu correo!", "success");
          } catch (error) {
            console.error(error);
            openSnackbar("¡Error al enviar el correo!", "error");
          }
        },
      },
      {
        label: "Cerrar Sesión",
        fn: () => dispatch(logOut()),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [openSnackbar, profile?.email]
  );

  const handleOrganizationChange = (e: SelectChangeEvent<unknown>) => {
    dispatch(setActiveOrganization(e.target.value as string));
    navigate(routes.organizations.details.replace(":organizationId", e.target.value as string));
  };

  const width = useMemo(() => {
    if (!isSidebarExpanded) return 0;
    if (isMobile) return "100%";
    return 300;
  }, [isSidebarExpanded, isMobile]);

  const isCurrentOrganizationLoaded = useMemo(() => {
    if (!organizationId) return false;
    const orgIds = Object.keys(organizations);
    return orgIds.includes(organizationId);
  }, [organizationId, organizations]);

  return (
    <Drawer
      open={isSidebarExpanded}
      sx={{
        width,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width,
          boxSizing: "border-box",
          backgroundColor: "#06175D",
          color: "white",
        },
      }}
      variant="persistent"
      anchor="left"
    >
      <Stack py={3} px={2} direction="row" justifyContent="space-between" alignItems="center">
        <img src={Logo} alt="BiteLink" style={{ height: "43px" }} />
        {isMobile ? (
          <Button onClick={() => setIsSidebarExpanded(false)}>
            <X />
          </Button>
        ) : null}
      </Stack>

      {organizationId ? (
        <Box px={2}>
          <Select
            id="organizations-picker"
            value={isCurrentOrganizationLoaded ? organizationId : ""}
            onChange={handleOrganizationChange}
            fullWidth={true}
            IconComponent={ChevronDown}
            disabled={!organizations}
            data-tour="organizations-list"
          >
            {!isCurrentOrganizationLoaded ? (
              <MenuItem dense value="" disabled>
                Cargando...
              </MenuItem>
            ) : (
              Object.entries(organizations).map(([organizationId, organization]) => (
                <MenuItem dense value={organizationId} key={organizationId}>
                  {organization.title}
                </MenuItem>
              ))
            )}
            {hasAccess("create.organization") ? (
              <>
                <Divider />
                <Box paddingX={2} paddingY={1}>
                  <Button
                    fullWidth
                    color="success"
                    size="small"
                    onClick={() => {
                      navigate(routes.organizations.create);
                    }}
                  >
                    Crear Organización
                  </Button>
                </Box>
              </>
            ) : null}
          </Select>
        </Box>
      ) : null}

      <MenuList sx={{ paddingX: 2, flex: 1 }}>
        {sidebarMenu.map((menu) => (
          <MenuItem
            key={menu.slug}
            selected={isActive(menu.slug)}
            onClick={() => {
              navigate(`/comercios/${organizationId}/${menu.slug}`);
              if (isMobile) {
                setIsSidebarExpanded(false);
              }
            }}
            data-tour={menu?.dataTour}
          >
            <ListItemIcon>{menu.icon}</ListItemIcon>
            <ListItemText>{menu.label}</ListItemText>
          </MenuItem>
        ))}
      </MenuList>

      <Stack>
        <Divider color={"#1C2C6B"} />
        <Stack p={2} direction={"row"} alignItems={"center"} justifyContent={"space-between"}>
          <Typography>
            {profile.name} {profile.lastname}
          </Typography>
          <IconButton
            sx={{
              border: 1,
              borderColor: "white",
            }}
            onClick={(event: React.MouseEvent<HTMLElement>) => setAnchorElUser(event.currentTarget)}
            data-tour="user-menu"
          >
            <User color="white" />
          </IconButton>
        </Stack>
      </Stack>
      <Menu
        sx={{ mt: "45px" }}
        id="menu-appbar"
        anchorEl={anchorElUser}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        keepMounted
        transformOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        open={Boolean(anchorElUser)}
        onClose={() => setAnchorElUser(null)}
      >
        {userSettings.map((setting) => (
          <MenuItem key={setting.label} onClick={setting.fn ?? undefined}>
            <Typography textAlign="center">{setting.label}</Typography>
          </MenuItem>
        ))}
      </Menu>
    </Drawer>
  );
};

export default Sidebar;
